import React, { useState } from "react";
import {
  Button,
  Form,
  Input,
  DatePicker,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  UserOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DobPopup from "../DobPopup";

dayjs.extend(customParseFormat);

const { Title, Text } = Typography;
interface StepPersonalInfoProps {
  setCurrentStep: (step: number) => void;
  setProfileData: (data: any) => void;
  profileData: any;
  setCompletedSteps: (steps: number[]) => void;
  completedSteps: number[];
  setFirstName: (data: string) => void
  firstName: string
  setLastName: (data: string) => void
  lastName: string
  setBirthday: (DataTransfer: string | string[] | undefined) => void
  birthday: string | string[] | undefined
}

const StepPersonalInfo: React.FC<StepPersonalInfoProps> = ({
  setCurrentStep,
  setProfileData,
  profileData,
  setCompletedSteps,
  completedSteps,
  setFirstName,
  setLastName,
  setBirthday,
  firstName,
  lastName,
  birthday
}) => {
  const [form] = Form.useForm();
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: any) => {
    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const params = {
        firstName: values.firstName,
        lastName: values.lastName,
        fullname: `${values.firstName} ${values.lastName}`,
        birthday: values.birthday ? formatDate(values.birthday.$d) : "",
      };

      setProfileData({
        ...profileData,
        ...params,
      });

      if (!completedSteps.includes(0)) {
        setCompletedSteps([...completedSteps, 0]);
      }

      message.success(
        "Your personal information has been submitted successfully!"
      );
      setLoading(false);
      setCurrentStep(1);
    }, 1500);
  };

  const handleFirstNameChange = (e: any) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e: any) => {
    setLastName(e.target.value);
  };

  return (
    <div className="personal-info-step">
      <Title level={3} className="text-white text-start mb-2">
        Basic Information
      </Title>
      <Text className="text-white block text-start mb-4 text-lg">
        Please provide your personal information to complete your profile. <br />
        <span className="text-orange-400">Don't worry, you can update this information later if needed.</span>
      </Text>

      <Form
        form={form}
        name="personal_info_form"
        onFinish={onFinish}
        layout="vertical"
        size="large"
        className="personal-info-form"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:!gap-4">
          <Form.Item
            name="firstName"
            rules={[{ required: false, message: "First name is required!" }]}
          >
            <Input
              prefix={<UserOutlined className="text-gray-400" />}
              placeholder="Your first name"
              onChange={handleFirstNameChange}
              className="custom-input !rounded-full !bg-transparent !text-white"
            />
          </Form.Item>

          <Form.Item
            name="lastName"
            rules={[{ required: false, message: "Last name is required!" }]}
          >
            <Input
              prefix={<UserOutlined className="text-gray-400" />}
              placeholder="Your last name"
              onChange={handleLastNameChange}
              className="custom-input !rounded-full !bg-transparent !text-white"
            />
          </Form.Item>
        </div>

        {(firstName || lastName) && (
          <div className="mb-2 border-2 border-dashed border-blue-400 bg-blue-400/10 text-start px-4 py-1 rounded-lg mb-4">
            <div className="flex items-center space-x-1">
              <div className="text-gray-200 text-sm font-medium">
                Name Preview:
              </div>
              <span className="text-lg font-semibold text-blue-400 p-0">
                {lastName} {firstName}
              </span>
            </div>
            <small className="text-gray-200">
              This is how your name will appear on your profile
            </small>
          </div>
        )}

        <div className="!flex !flex-col md:!flex-row items-start md:!items-center justify-between">
          <Form.Item
            name="birthday"
            rules={[{ required: false, message: "Birthday is required!" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  const today = dayjs();
                  const age = today.diff(value, "year");
                  if (age >= 16) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("You must be at least 16 years old"));
                },
              }),
            ]}
            className="!m-0 !mb-4 md:!mb-0 !mr-5 md:flex-1 w-full"
          >
            <DatePicker
              style={{ width: "100%", height: "48px" }}
              format={["DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY", "DD MM YYYY"]}
              placeholder="DD/MM/YYYY"
              onChange={(date, dateString) => setBirthday(dateString)}
              disabledDate={(current) => {
                return current && current > dayjs().endOf("day");
              }}
              inputReadOnly={false}
              allowClear={true}
              className="custom-input !bg-transparent border-white/30 !rounded-full text-white hover:border-blue-400 focus:border-blue-400"
            />
          </Form.Item>
          <Tooltip title="Click to learn more about why we need your date of birth" className="flex-1">
            <div onClick={() => setShowPopup(true)}>
              <span className="text-white mr-2 hover:underline">
                Why do we need your date of birth?
              </span>
              <InfoCircleOutlined className="!text-white" />
            </div>
          </Tooltip>
        </div>

        <div className="flex gap-4 mt-8">
          {/* <Button
            type="default"
            onClick={handleSkip}
            className="flex-1 h-12 bg-transparent border-white/30 text-white hover:bg-white/10 hover:border-white/50"
          >
            Skip for now
          </Button> */}
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            className="flex-1 h-12 !bg-transparent !border-[#E9B460] hover:!bg-[#e9b460]/[.8] border-blue-600 hover:border-blue-700"
          >
            Continue
          </Button>
        </div>
      </Form>

      {showPopup && (
        <DobPopup onClose={() => setShowPopup(false)} isOpen={true} />
      )}
    </div>
  );
};

export default StepPersonalInfo;
